import { computed, ref } from 'vue';
import { UserState } from '@/composables/User';

export type OnboardingStepId =
  | 'userInfos'
  | 'connect'
  | 'shopConfirm'
  | 'liftOff';

export interface OnboardingStep {
  id: OnboardingStepId;
  component: string;
}

const ALL_ONBOARDING_STEPS: readonly OnboardingStep[] = [
  { id: 'userInfos', component: 'UserInfosStep' },
  { id: 'connect', component: 'ConnectStep' },
  { id: 'shopConfirm', component: 'ShopConfirmStep' },
  { id: 'liftOff', component: 'LiftOffStep' },
] as const;

// Le subUser n'est pas l'owner du shop actif : on retire la step `userInfos`
// (profil utilisateur), qui est user-scoped et ne le concerne pas.
const isSubUserOfActiveShop = (): boolean => {
  const activeShop = UserState.activeShop;
  const user = UserState.user;
  if (!activeShop || !user) return false;
  if (user.userType === 'admin') return false;
  return activeShop.idUser !== user.id;
};

export const getOnboardingSteps = (): readonly OnboardingStep[] => {
  if (isSubUserOfActiveShop()) {
    return ALL_ONBOARDING_STEPS.filter((s) => s.id !== 'userInfos');
  }
  return ALL_ONBOARDING_STEPS;
};

const STORAGE_KEY = 'spm_onboarding_v2_step';

const readPersistedIndex = (): number => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return 0;
    const parsed = Number.parseInt(raw, 10);
    const max = getOnboardingSteps().length - 1;
    if (Number.isNaN(parsed) || parsed < 0 || parsed > max) {
      return 0;
    }
    return parsed;
  } catch {
    return 0;
  }
};

const persistIndex = (index: number): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(index));
  } catch {
    // sessionStorage indisponible (incognito strict, quota) → non bloquant
  }
};

const currentIndex = ref<number>(readPersistedIndex());

export const getPersistedIndex = readPersistedIndex;

export const useOnboardingState = () => {
  const steps = computed(() => getOnboardingSteps());
  const totalSteps = computed(() => steps.value.length);

  const currentStep = computed<OnboardingStep>(
    () => steps.value[Math.min(currentIndex.value, steps.value.length - 1)],
  );

  const isFirst = computed(() => currentIndex.value === 0);
  const isLast = computed(() => currentIndex.value === totalSteps.value - 1);

  const setIndex = (index: number) => {
    if (index < 0 || index >= totalSteps.value) return;
    currentIndex.value = index;
    persistIndex(index);
  };

  const goNext = () => {
    if (isLast.value) return;
    setIndex(currentIndex.value + 1);
  };

  const goPrev = () => {
    if (isFirst.value) return;
    setIndex(currentIndex.value - 1);
  };

  const goTo = (id: OnboardingStepId) => {
    const index = steps.value.findIndex((step) => step.id === id);
    if (index === -1) return;
    setIndex(index);
  };

  const reset = () => {
    setIndex(0);
  };

  const clearPersisted = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    currentIndex.value = 0;
  };

  return {
    totalSteps,
    currentIndex,
    currentStep,
    isFirst,
    isLast,
    setIndex,
    goNext,
    goPrev,
    goTo,
    reset,
    clearPersisted,
  };
};
